import { useInfiniteQuery, useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, type Ref, toValue } from "vue";
import { useApi } from "@shopexperts/shared";
import type {
  ExpertClaimDecisionFilter,
  ExpertClaimStatus,
  ExpertClaimType,
} from "@shopexperts/legacy-api";

interface UseAdminExpertClaimsOptions {
  search?: MaybeRefOrGetter<string>;
  decision?: MaybeRefOrGetter<string>;
  status?: MaybeRefOrGetter<string>;
  type?: MaybeRefOrGetter<string>;
  limit?: number;
}

export function useAdminExpertClaims(options: UseAdminExpertClaimsOptions = {}) {
  const api = useApi();
  const {
    search = "",
    decision = "",
    status = "",
    type = "",
    limit = 25,
  } = options;

  const searchValue = computed(() => toValue(search));
  const decisionValue = computed(() => toValue(decision));
  const statusValue = computed(() => toValue(status));
  const typeValue = computed(() => toValue(type));

  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: computed(() => [
      "admin", "expert-claims",
      {
        search: searchValue.value,
        decision: decisionValue.value,
        status: statusValue.value,
        type: typeValue.value,
      },
    ]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.expertClaims.list({
        query: {
          limit,
          cursor: pageParam,
          decision: (decisionValue.value || undefined) as ExpertClaimDecisionFilter | undefined,
          status: (statusValue.value || undefined) as ExpertClaimStatus | undefined,
          type: (typeValue.value || undefined) as ExpertClaimType | undefined,
          search: searchValue.value || undefined,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const claims = computed(() => {
    return data.value?.pages.flatMap((page) => page.data) || [];
  });

  return {
    claims,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  };
}

/**
 * Single-claim detail query for the drawer. Keyed under the same
 * ['admin', 'expert-claims'] prefix so decision mutations invalidate it
 * together with the list — a decided claim that leaves a filtered list
 * still refreshes in the open drawer.
 */
export function useAdminExpertClaimDetail(claimId: Ref<number | null>) {
  const api = useApi();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: computed(() => ["admin", "expert-claims", "detail", claimId.value]),
    queryFn: async () => {
      return await api.admin.expertClaims.get({
        params: { claimId: claimId.value as number },
      });
    },
    enabled: computed(() => claimId.value !== null),
    staleTime: 30 * 1000,
  });

  const claim = computed(() => data.value?.claim ?? null);

  return { claim, isLoading, isFetching, error, refetch };
}
